// A node's local MCP is OFF or ON, with no tiers (router docs/PERMISSIONS.md § 8, router#1762).
//
// The state lives in the reserved role.* labels that only the router's node_grant / node_revoke
// write (a DB trigger refuses a label write from the app). The app reads them to SHOW the state.
//
// ⚠⚠ The app must work on the router that is live now AND on the router that carries router#1762,
// because the app deploys on merge and the router deploys on its own release:
//   - ON is `node_grant` with `role: "manage"`. The new router accepts "manage" as the old
//     spelling of on. The old router reads it as its manage tier, which writes the same label.
//     Do NOT omit `role`: the old router refuses a grant with no role.
//   - `role: "observe"` is refused by the new router. The app never sends it.
//   - OFF is `node_revoke` on both routers. It also clears the retired observe label.
export const ROLE_ON = 'role.mcp-manage'
export const ROLE_RETIRED_OBSERVE = 'role.mcp-observe'
export const ROLE_TARGETS = 'role.mcp-targets'
export const ROLE_KEYS = [ROLE_ON, ROLE_RETIRED_OBSERVE, ROLE_TARGETS]

// The role value the app sends to node_grant. See the note above.
export const GRANT_ROLE_ON = 'manage'

type Labels = Record<string, unknown> | null | undefined

// The router's labelIsTrue (router src/db/node_selector.go): a boolean true, or the string
// "true", "1" or "yes". The app must read a label as the router enforces it. If the app read only a
// boolean, a node with the string form would show OFF while its local MCP runs.
export function labelIsTrue(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return value === 'true' || value === '1' || value === 'yes'
  return false
}

// On only when the on label is set. A node that holds only the retired observe label is OFF: § 8
// migrates observe to off, and the new router's MCPGrantFor reads only the on label.
export function localMcpOn(labels: Labels): boolean {
  return labelIsTrue(labels?.[ROLE_ON])
}

// True when the node holds the retired observe label without the on label. The app shows it as
// off and says why, so that the change in what the owner sees is not silent.
export function heldRetiredObserve(labels: Labels): boolean {
  return !localMcpOn(labels) && labelIsTrue(labels?.[ROLE_RETIRED_OBSERVE])
}

// The target selector, as the text field shows it. Empty means no limit.
export function localMcpTargets(labels: Labels): string {
  const targets = labels?.[ROLE_TARGETS]
  return targets ? String(targets) : ''
}

// Where the local MCP acts, in words. `code` is a network name, which the page shows as code.
// `networkCount` is every network of the node and `loadedNames` the ones the app loaded: it loads no
// network of another org. A node in no network has no peers, so it reaches only itself.
export function localMcpReach(networkCount: number, loadedNames: string[]): { text: string; code?: string } {
  if (!networkCount) return { text: 'this node' }
  if (networkCount === 1 && loadedNames.length === 1) return { text: 'the nodes of ', code: loadedNames[0] }
  return { text: 'the nodes of its networks' }
}

// The labels the router writes, for an optimistic update. The realtime nodes UPDATE confirms it.
// Both routers remove every role.* key and then set the on label and the targets, if any.
export function labelsAfter(labels: Labels, on: boolean, targets: string): Record<string, unknown> {
  const next: Record<string, unknown> = { ...labels }
  for (const key of ROLE_KEYS) delete next[key]
  if (on) {
    next[ROLE_ON] = true
    const trimmed = targets.trim()
    if (trimmed) next[ROLE_TARGETS] = trimmed
  }
  return next
}

// The node_grant arguments that turn the local MCP on. node_grant clears role.mcp-targets on
// EVERY write, so a change to the targets must send the full grant again, and a grant must send
// the current targets.
export function grantArgs(nodeId: string, targets: string): { node_ids: string[]; role: string; targets?: string } {
  const trimmed = targets.trim()
  return trimmed
    ? { node_ids: [nodeId], role: GRANT_ROLE_ON, targets: trimmed }
    : { node_ids: [nodeId], role: GRANT_ROLE_ON }
}
